import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './Button';

export interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
  showHandle?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
}

export const BottomSheet: React.FC<BottomSheetProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  className = '',
  showHandle = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && closeOnEscape) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, closeOnEscape, onClose]);

  if (!isOpen) return null;

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-end"
      onClick={closeOnOverlayClick ? onClose : undefined}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'bottom-sheet-title' : undefined}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        ref={contentRef}
        className={`bottom-sheet w-full max-w-screen-xl ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {showHandle && (
          <div className="bottom-sheet-handle" aria-hidden="true" />
        )}
        {(title || subtitle) && (
          <div className="mb-4" id="bottom-sheet-title">
            {title && <h2 className="font-headline-md">{title}</h2>}
            {subtitle && <p className="font-body-sm text-on-surface-variant mt-1">{subtitle}</p>}
          </div>
        )}
        {children}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'primary';
  isLoading?: boolean;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'primary',
  isLoading = false,
}) => {
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      closeOnOverlayClick={!isLoading}
      closeOnEscape={!isLoading}
    >
      <p className="font-body-md text-on-surface mb-6">{message}</p>
      <div className="flex gap-3 w-full">
        <Button
          variant="outline"
          fullWidth
          onClick={onClose}
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          variant={variant === 'danger' ? 'secondary' : 'primary'}
          fullWidth
          onClick={onConfirm}
          disabled={isLoading}
          isLoading={isLoading}
        >
          {confirmText}
        </Button>
      </div>
    </BottomSheet>
  );
};